/**
 * Avatar Upload Component
 * Upload and manage profile picture with drag-and-drop
 */

import { useState, useRef } from 'react'
import { Upload, Camera, Trash2, X, Check, AlertCircle } from 'lucide-react'
import { useUploadAvatar, useRemoveAvatar } from '@/hooks/useProfile'
import { toast } from '@/lib/toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface AvatarUploadProps {
  open: boolean
  onClose: () => void
  currentAvatar: string
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']

export function AvatarUpload({ open, onClose, currentAvatar }: AvatarUploadProps) {
  const uploadAvatar = useUploadAvatar()
  const removeAvatar = useRemoveAvatar()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string>('')

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (file: File) => {
    setError('')

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, or GIF)')
      return
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 5MB')
      return
    }

    setSelectedFile(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      await uploadAvatar.mutateAsync(selectedFile)
      toast.success('Profile picture updated successfully')
      handleClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message
      toast.error('Failed to upload profile picture', errorMessage)
    }
  }

  const handleRemove = async () => {
    if (!confirm('Are you sure you want to remove your profile picture?')) {
      return
    }

    try {
      await removeAvatar.mutateAsync()
      toast.success('Profile picture removed')
      handleClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message
      toast.error('Failed to remove profile picture', errorMessage)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setError('')
    setIsDragging(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  const isLoading = uploadAvatar.isPending || removeAvatar.isPending

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <Camera className="h-5 w-5" />
            Profile Picture
          </DialogTitle>
          <DialogDescription>
            Upload a new profile picture or remove the current one
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Current Avatar */}
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700 mb-3">Current Picture</p>
            <img
              src={currentAvatar}
              alt="Current avatar"
              className="w-32 h-32 rounded-full object-cover border-4 border-primary/20 mx-auto"
            />
          </div>

          {/* Upload Area */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Upload New Picture</p>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 hover:border-primary hover:bg-gray-50'
              }`}
            >
              {previewUrl ? (
                <div className="space-y-3">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-32 h-32 rounded-full object-cover border-4 border-primary/20 mx-auto"
                  />
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <Check className="h-4 w-4" />
                    <span className="text-sm font-medium">Image selected</span>
                  </div>
                  <p className="text-xs text-gray-500">{selectedFile?.name}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 mx-auto text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">
                      Drop an image here or click to browse
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      JPG, PNG or GIF (max 5MB)
                    </p>
                  </div>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* File Requirements */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Image requirements:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center gap-2">
                <Check className="h-3 w-3 text-gray-400" />
                Formats: JPG, PNG, or GIF
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3 w-3 text-gray-400" />
                Maximum size: 5MB
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3 w-3 text-gray-400" />
                Recommended: Square image, at least 200x200 pixels
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleRemove}
              disabled={isLoading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Current
            </Button>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile || isLoading}
                className="bg-primary"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadAvatar.isPending ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
